/**
 * Firebase Cloud Messaging (Web Push) — capa cliente.
 *
 *  - `isPushSupported`: comprueba que el navegador soporta SW + Notifications.
 *  - `enableWebPush(uid)`: pide permiso, registra SW, obtiene token y lo
 *    persiste en `users/{uid}/fcmTokens/{tokenHash}`.
 *  - `disableWebPush(uid)`: borra el token del dispositivo actual.
 *  - `subscribeForegroundMessages(cb)`: dispara cuando llega un push y la
 *    pestaña está activa (FCM no muestra notificación nativa en ese caso).
 */

import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
  deleteToken,
  type Messaging,
  type MessagePayload,
} from "firebase/messaging";
import { doc, serverTimestamp, setDoc, deleteDoc } from "firebase/firestore";
import app, { db } from "@/lib/firebase";

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined;
const SW_FILENAME = "/firebase-messaging-sw.js";

const SW_CONFIG_QS = new URLSearchParams({
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "",
}).toString();

let _messaging: Messaging | null = null;
let _swRegistration: ServiceWorkerRegistration | null = null;

/** ¿Este navegador soporta web push? */
export async function isPushSupported(): Promise<boolean> {
  try {
    if (typeof window === "undefined") return false;
    if (!("Notification" in window)) return false;
    if (!("serviceWorker" in navigator)) return false;
    return await isSupported();
  } catch {
    return false;
  }
}

/** Configurada la VAPID key vía .env. */
export function isPushConfigured(): boolean {
  return Boolean(VAPID_KEY);
}

async function ensureMessaging(): Promise<Messaging | null> {
  if (_messaging) return _messaging;
  if (!(await isPushSupported())) return null;
  _messaging = getMessaging(app);
  return _messaging;
}

async function ensureServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (_swRegistration) return _swRegistration;
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return null;
  }
  _swRegistration = await navigator.serviceWorker.register(
    `${SW_FILENAME}?${SW_CONFIG_QS}`,
    { scope: "/" },
  );
  return _swRegistration;
}

/** Hash simple del token para usarlo como docId estable (no PII). */
async function tokenHash(token: string): Promise<string> {
  const data = new TextEncoder().encode(token);
  const buf = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(buf))
    .slice(0, 16)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export type EnablePushResult =
  | { ok: true; token: string }
  | {
      ok: false;
      reason: "unsupported" | "not_configured" | "denied" | "error";
      error?: unknown;
    };

/** Pide permiso y registra el token en Firestore. */
export async function enableWebPush(uid: string): Promise<EnablePushResult> {
  if (!(await isPushSupported())) return { ok: false, reason: "unsupported" };
  if (!isPushConfigured()) return { ok: false, reason: "not_configured" };

  try {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return { ok: false, reason: "denied" };

    const messaging = await ensureMessaging();
    const swReg = await ensureServiceWorker();
    if (!messaging || !swReg) return { ok: false, reason: "unsupported" };

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swReg,
    });

    if (!token) return { ok: false, reason: "denied" };

    const tid = await tokenHash(token);
    await setDoc(
      doc(db, "users", uid, "fcmTokens", tid),
      {
        token,
        userAgent:
          typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
        platform:
          typeof navigator !== "undefined" && (navigator as any).platform
            ? (navigator as any).platform
            : "web",
        createdAt: serverTimestamp(),
        lastUsedAt: serverTimestamp(),
      },
      { merge: true },
    );

    return { ok: true, token };
  } catch (err) {
    console.error("[push] enable failed", err);
    return { ok: false, reason: "error", error: err };
  }
}

/** Borra el token de este dispositivo. */
export async function disableWebPush(uid: string): Promise<boolean> {
  try {
    const messaging = await ensureMessaging();
    if (messaging) {
      try {
        // Necesitamos el token para borrarlo del registro local + Firestore
        const swReg = await ensureServiceWorker();
        if (swReg && VAPID_KEY) {
          const token = await getToken(messaging, {
            vapidKey: VAPID_KEY,
            serviceWorkerRegistration: swReg,
          });
          if (token) {
            const tid = await tokenHash(token);
            await deleteDoc(doc(db, "users", uid, "fcmTokens", tid));
            await deleteToken(messaging);
          }
        }
      } catch (e) {
        console.warn("[push] deleteToken local failed", e);
      }
    }
    return true;
  } catch (err) {
    console.error("[push] disable failed", err);
    return false;
  }
}

/** Mensajes que llegan con la pestaña activa (FCM no muestra notif nativa). */
export async function subscribeForegroundMessages(
  cb: (payload: MessagePayload) => void,
): Promise<() => void> {
  const messaging = await ensureMessaging();
  if (!messaging) return () => undefined;
  return onMessage(messaging, cb);
}
